import { fetcher } from '../../../lib/api-fetcher';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';



export type VaultHistoryItem = {
  id: string;
  amountPaise: number;
  type: string;
  notes: string | null;
  agentId: string | null;
  createdAt: string;
  metadata?: any;
};

export type PendingHandover = {
  id: string;
  agentId: string;
  agentName?: string | null;
  expectedAmountPaise: number | null;
  amountPaise?: number;
  discrepancyPaise?: number;
  status: string;
  notes: string | null;
  rejectionReason?: string | null;
  createdAt: string;
};

/** Per-agent outstanding cash custody (HELD_BY_AGENT read model). */
export type AgentCashPosition = {
  agentId: string;
  userId: string;
  agentNumber: string;
  fullName: string;
  heldReceiptCount: number;
  heldAmountPaise: number;
};

export const vaultHooks = {
  useBalance: () => {
    return useQuery({
      queryKey: ['vault', 'balance'],
      queryFn: () => fetcher.get<{ balancePaise: number }>('/staff/v1/vault/balance'),
    });
  },

  useHistory: (page: number, limit: number) => {
    return useQuery({
      queryKey: ['vault', 'history', page, limit],
      queryFn: () => fetcher.get<{ data: VaultHistoryItem[] }>(`/staff/v1/vault/history?page=${page}&limit=${limit}`),
    });
  },

  useHandovers: (status?: string) => {
    return useQuery({
      queryKey: ['vault', 'handovers', status ?? 'all'],
      queryFn: () =>
        fetcher.get<{ data: PendingHandover[] }>(
          `/staff/v1/vault/handovers${status ? `?status=${encodeURIComponent(status)}` : ''}`,
        ),
      enabled: !!status,
    });
  },

  useAcceptHandover: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ handoverId, ...data }: { handoverId: string; actualAmountPaise: number; discrepancyReason?: string; notes?: string }) =>
        fetcher.post(`/staff/v1/vault/handovers/${handoverId}/accept`, data),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['vault'] });
      },
    });
  },

  useRejectHandover: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ handoverId, reason }: { handoverId: string; reason: string }) =>
        fetcher.post(`/staff/v1/vault/handovers/${handoverId}/reject`, { reason }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['vault'] });
      },
    });
  },

  useAgentCashPositions: (enabled: boolean) => {
    return useQuery({
      queryKey: ['vault', 'agent-positions'],
      queryFn: () =>
        fetcher.get<{ data: AgentCashPosition[]; totalHeldPaise: number }>('/staff/v1/vault/agent-positions'),
      enabled,
    });
  },

  useDeposit: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (data: { amountPaise: number; notes?: string }) =>
        fetcher.post('/staff/v1/vault/deposit', data),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['vault'] });
      },
    });
  },
};
